/**
 * Settings → Notifications (decision 84): one checkbox per category of in-app notification, and one for the account
 * emails (payment failed, plan ending, credits used up). Saved at once (PUT /api/notifications/settings).
 */
import { useEffect, useState } from 'react';
import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS, type NotificationPrefs } from '@vw/core/src/notifications';
import { Spinner, toast } from '@vw/ui';
import { api, ApiError } from '../api';

export function NotificationSettings() {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  useEffect(() => { api<{ prefs: NotificationPrefs }>('/api/notifications').then((r) => setPrefs(r.prefs), () => setPrefs(null)); }, []);
  if (!prefs) return <Spinner />;
  const save = async (key: keyof NotificationPrefs, value: boolean) => {
    const before = prefs;
    setPrefs({ ...prefs, [key]: value });
    try {
      setPrefs((await api<{ prefs: NotificationPrefs }>('/api/notifications/settings', { method: 'PUT', json: { [key]: value } })).prefs);
    } catch (e) {
      setPrefs(before);
      toast.error(e instanceof ApiError ? e.message : 'Could not save the setting');
    }
  };
  const box = (key: keyof NotificationPrefs, label: string, hint?: string) => (
    <label key={key} className="flex items-start gap-3 text-sm">
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[rgb(var(--primary))]" checked={prefs[key]} onChange={(e) => void save(key, e.target.checked)} />
      <span>{label}{hint && <span className="block text-xs text-muted-foreground">{hint}</span>}</span>
    </label>
  );
  return (
    <div className="space-y-3">
      {NOTIFICATION_CATEGORIES.map((c) => box(c, NOTIFICATION_CATEGORY_LABELS[c], c === 'billing' ? 'A failed payment is always shown.' : undefined))}
      <div className="border-t border-border pt-3">{box('email', 'Email me when a payment fails, my plan is ending or my credits run out', 'Everything else stays in the app.')}</div>
    </div>
  );
}
